import { useDispatch, useSelector } from 'react-redux';
import { IMusselHarvest } from '../../entities/growing.entities';
import { useState } from 'react';
import { TrackStatus } from '../../entities/general.entities';
import { showFeedback } from '../../store/ui/ui.actions';
import { sendSingleRequest } from '../../apis';
import { Modal } from 'antd';
import { Button, CloseIcon, Dropdown, Input, Subtitle } from '../shared';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { displayValueUnit, toSecond } from '../../lib/common.helpers';
import { selectAccount } from '../../store/auth/auth.selector';

interface Props {
  visible: boolean;
  lineID: number;
  seedingID: number;
  harvests: IMusselHarvest[];
  onClose: () => void;
}

const AddTrackStatusModal = ({
  visible,
  lineID,
  seedingID,
  harvests,
  onClose,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [harvestID, setHarvestID] = useState<number>();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(TrackStatus.HARVESTED);
  const [loading, setLoading] = useState(false);

  const harvestOptions = [
    { id: 'undefined', value: '', label: translate(lang, 'Unconfirmed') },
    ...harvests.map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.id} - ${displayValueUnit(x.amount, unitType, 'weight_kg')}`,
    })),
  ];
  const statusOptions = [
    TrackStatus.HARVESTED,
    TrackStatus.OFFLOADED_FROM_BOAT,
    TrackStatus.LOADED_TO_COLD_STORAGE,
  ].map(x => ({ id: x, value: x, label: x }));

  const setError = (message: string) =>
    dispatch(showFeedback({ isMessageModal: true, message, type: 'error' }));

  const confirmClick = async () => {
    if (harvests?.some(x => x.is_full) && !harvestID) {
      setError('_sel_harvest_id');
      return;
    }
    if (!/^[a-f0-9]{32}$/.test(code)) {
      setError('_invalid_qr_code_value');
      return;
    }
    setLoading(true);
    let response = await sendSingleRequest(
      {
        track_id: code,
        data: [
          { line_id: lineID, seeding_id: seedingID, harvest_id: harvestID },
        ],
      },
      'POST',
      'api/task/track-lines',
      true,
    );
    if (!response.status) {
      setLoading(false);
      setError(response.data?.message ?? '_err_adding_track_line');
      return;
    }
    response = await sendSingleRequest(
      { track_id: code, status, tracked_time: toSecond(Date.now()) },
      'POST',
      'api/task/track-status',
      true,
    );
    setLoading(false);
    if (!response.status) {
      setError(response.data?.message ?? '_err_adding_track_status');
      return;
    }
    dispatch(
      showFeedback({
        isMessage: true,
        message: translate(lang, '_qr_code_added_ok'),
        type: 'success',
      }),
    );
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      footer={null}
    >
      <div className='section-modal wrap'>
        <div className='d-flex align-center mb-17'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Add Scanned QR Track')}
          </Subtitle>
        </div>
        <div className='section'>
          <div className='mb-17'>
            <Dropdown
              label={translate(lang, 'Harvest ID')}
              options={harvestOptions}
              value={harvestID?.toString()}
              onChange={v =>
                setHarvestID(v.length <= 0 ? undefined : Number(v))
              }
            />
          </div>
          <div className='mb-17'>
            <Input
              type='text'
              label={translate(lang, 'Track Code Value')}
              value={code}
              onChange={e => setCode(e.target.value)}
            />
          </div>
          <div className='mb-17'>
            <Dropdown
              label={translate(lang, 'Status')}
              options={statusOptions}
              value={status}
              onChange={v => setStatus(v)}
            />
          </div>
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width={'small'}
            className='rsp-btn'
            size={2}
            type='fill'
            color='red'
            onClick={onClose}
            disabled={loading}
          >
            {translate(lang, 'Close')}
          </Button>
          <Button
            width={'small'}
            className='rsp-btn ml-16'
            size={2}
            type='fill'
            color='green'
            onClick={confirmClick}
            disabled={loading}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default AddTrackStatusModal;
